<template>
  <div class="container mt-5" v-if="user">
    <b-card class="profile-card">

      <div class="profile-header text-center mb-4">
        <div class="avatar mx-auto mb-3">{{ initials }}</div>
        <h4 class="mb-2">{{ fullName }}</h4>
        <b-badge :variant="roleBadgeVariant">{{ roleLabel }}</b-badge>
      </div>

      <hr />

      <div class="profile-info">
        <div class="info-row" v-if="user.name">
          <b-icon icon="person" class="info-icon"></b-icon>
          <div>
            <div class="info-label">Prénom</div>
            <div class="info-value">{{ user.name }}</div>
          </div>
        </div>

        <div class="info-row" v-if="user.surname">
          <b-icon icon="person-lines-fill" class="info-icon"></b-icon>
          <div>
            <div class="info-label">Nom</div>
            <div class="info-value">{{ user.surname }}</div>
          </div>
        </div>

        <div class="info-row" v-if="user.email">
          <b-icon icon="envelope" class="info-icon"></b-icon>
          <div>
            <div class="info-label">Email</div>
            <div class="info-value">{{ user.email }}</div>
          </div>
        </div>

        <div class="info-row" v-if="user.phoneNumber">
          <b-icon icon="telephone" class="info-icon"></b-icon>
          <div>
            <div class="info-label">Téléphone</div>
            <div class="info-value">{{ user.phoneNumber }}</div>
          </div>
        </div>
      </div>

      <hr />

      <div class="notifications-section">
        <div class="text-muted small notifications-title">Notifications</div>

        <div class="notif-row">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <div class="info-label mb-0">Newsletter</div>
              <div class="info-value small text-muted">Recevoir les actualités du club par email</div>
            </div>
            <b-form-checkbox
              v-model="newsletterEnabled"
              switch
              :disabled="newsletterLoading"
              @change="toggleNewsletter"
            />
          </div>
          <b-alert v-if="newsletterError" variant="danger" show class="mt-2 mb-0 py-2 small">{{ newsletterError }}</b-alert>
        </div>

        <div class="notif-row">
          <div>
            <div class="info-label mb-0">Nouvelle ouverture</div>
            <div class="info-value small text-muted">Recevoir un email si une nouvelle ouverture a lieu l'un de ces jours</div>
          </div>
          <div class="day-picker mt-2">
            <button
              v-for="day in weekDays"
              :key="day.value"
              type="button"
              class="day-chip"
              :class="{ active: newOuvertureNotifDays.includes(day.value), disabled: newOuvertureNotifLoading }"
              :disabled="newOuvertureNotifLoading"
              @click="toggleNewOuvertureDay(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
          <b-alert v-if="newOuvertureNotifError" variant="danger" show class="mt-2 mb-0 py-2 small">{{ newOuvertureNotifError }}</b-alert>
        </div>

        <div class="notif-row">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <div class="info-label mb-0">Inscription covoiturage</div>
              <div class="info-value small text-muted">Recevoir un email quand quelqu'un s'inscrit à mon covoit</div>
            </div>
            <b-form-checkbox
              v-model="covoitInscriptionNotifEnabled"
              switch
              :disabled="covoitInscriptionNotifLoading"
              @change="toggleCovoitInscriptionNotif"
            />
          </div>
          <b-alert v-if="covoitInscriptionNotifError" variant="danger" show class="mt-2 mb-0 py-2 small">{{ covoitInscriptionNotifError }}</b-alert>
        </div>
      </div>

      <hr />

      <div class="quick-links">
        <div class="text-muted small mb-3 ">Accès rapide</div>
        <div class="d-flex flex-wrap justify-content-center"  style="gap: 0.5rem">
          <b-button-group class="w-100">
          <b-button variant="outline-primary" size="sm" @click="$router.push('/mes-inscriptions')">
            <b-icon icon="calendar-check" class="mr-1"></b-icon>
            Mes inscriptions
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="$router.push('/mes-covoiturages')">
            <b-icon icon="car-front" class="mr-1"></b-icon>
            Mes covoits
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="$router.push('/adhesion')">
            <b-icon icon="card-checklist" class="mr-1"></b-icon>
            Mon adhésion
          </b-button>
          <b-button  v-if="isOuvreurOrAdmin" variant="outline-primary" size="sm" @click="$router.push('/mes-ouvertures')">
            <b-icon icon="calendar-plus" class="mr-1"></b-icon>
            Mes ouvertures
          </b-button>
          </b-button-group>
        </div>
        <div class="d-flex mt-4 flex-column">
          <b-button variant="outline-primary" size="sm" @click="$router.push('/mon-historique')" class="mb-2">
            <b-icon icon="clock-history" class="mr-1"></b-icon>
            Mon historique
          </b-button>
          <b-button v-if="!isOuvreurOrAdmin" variant="outline-primary" size="sm" @click="$router.push('/mes-ouvertures')">
            <b-icon icon="person" class="mr-1"></b-icon>
            Devenir ouvreur
          </b-button>
        </div>
      </div>
    </b-card>
  </div>

  <div v-else class="text-center p-5">
    <b-spinner label="Chargement..."></b-spinner>
  </div>
</template>

<script>
import session from "@/store/session";
import { UserService } from "@/services/user.service";

export default {
  name: "Profile",
  data() {
    return {
      newsletterLoading: false,
      newsletterError: null,
      newsletterEnabled: session.user ? !!session.user.newsletter : false,
      newOuvertureNotifDays: session.user ? [...(session.user.notifyNewOuvertureDays || [])] : [],
      newOuvertureNotifLoading: false,
      newOuvertureNotifError: null,
      covoitInscriptionNotifEnabled: session.user ? !!session.user.notifyCovoitInscription : false,
      covoitInscriptionNotifLoading: false,
      covoitInscriptionNotifError: null,
      weekDays: [
        { value: "lundi", label: "Lun" },
        { value: "mardi", label: "Mar" },
        { value: "mercredi", label: "Mer" },
        { value: "jeudi", label: "Jeu" },
        { value: "vendredi", label: "Ven" },
        { value: "samedi", label: "Sam" },
        { value: "dimanche", label: "Dim" },
      ],
    };
  },
  computed: {
    user() {
      return session.user;
    },
    fullName() {
      return [this.user.name, this.user.surname].filter(Boolean).join(" ").trim() || "—";
    },
    initials() {
      const n = (this.user.name || "").charAt(0).toUpperCase();
      const s = (this.user.surname || "").charAt(0).toUpperCase();
      return (n + s) || "?";
    },
    roleLabel() {
      const labels = {
        utilisateur: "Membre",
        ouvreur: "Ouvreur",
        admin: "Administrateur",
        desactive: "Désactivé",
      };
      return labels[this.user.role] || this.user.role;
    },
    roleBadgeVariant() {
      const variants = {
        utilisateur: "secondary",
        ouvreur: "primary",
        admin: "warning",
        desactive: "danger",
      };
      return variants[this.user.role] || "secondary";
    },
    isOuvreurOrAdmin() {
      return ["ouvreur", "admin"].includes(this.user.role);
    },
  },
  methods: {
    async toggleNewOuvertureDay(day) {
      const previousDays = [...this.newOuvertureNotifDays];
      const index = this.newOuvertureNotifDays.indexOf(day);
      if (index === -1) {
        this.newOuvertureNotifDays.push(day);
      } else {
        this.newOuvertureNotifDays.splice(index, 1);
      }

      this.newOuvertureNotifLoading = true;
      this.newOuvertureNotifError = null;
      try {
        await UserService.updateNotifyNewOuvertureDays(this.newOuvertureNotifDays);
        session.setUser({ ...session.user, notifyNewOuvertureDays: [...this.newOuvertureNotifDays] });
      } catch (err) {
        this.newOuvertureNotifError = err.message || "Erreur lors de la mise à jour.";
        this.newOuvertureNotifDays = previousDays;
      } finally {
        this.newOuvertureNotifLoading = false;
      }
    },
    async toggleNewsletter(value) {
      this.newsletterLoading = true;
      this.newsletterError = null;
      try {
        await UserService.updateNewsletter(value);
        session.setUser({ ...session.user, newsletter: value });
      } catch (err) {
        this.newsletterError = err.message || "Erreur lors de la mise à jour.";
        this.newsletterEnabled = !value;
      } finally {
        this.newsletterLoading = false;
      }
    },
    async toggleCovoitInscriptionNotif(value) {
      this.covoitInscriptionNotifLoading = true;
      this.covoitInscriptionNotifError = null;
      try {
        await UserService.updateNotifyCovoitInscription(value);
        session.setUser({ ...session.user, notifyCovoitInscription: value });
      } catch (err) {
        this.covoitInscriptionNotifError = err.message || "Erreur lors de la mise à jour.";
        this.covoitInscriptionNotifEnabled = !value;
      } finally {
        this.covoitInscriptionNotifLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 700px;
}

.profile-card {
  border-radius: 0.75rem;
}

.avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: var(--color-guc-darkblue);
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 1px;
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.info-icon {
  color: var(--color-guc-darkblue);
  font-size: 1.1rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.info-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1px;
}

.info-value {
  font-size: 0.80rem;
  font-weight: 500;
  color: #343a40;
}

.quick-links .text-muted {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.notifications-title {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.notifications-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notif-row {
  background-color: #f8f9fa;
  border: 1px solid #edf0f2;
  border-radius: 0.6rem;
  padding: 0.75rem 0.9rem;
}

.day-picker {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
}

.day-chip {
  border: 1px solid #ced4da;
  background-color: #fff;
  color: #495057;
  border-radius: 1rem;
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.day-chip:hover {
  border-color: var(--color-guc-darkblue);
}

.day-chip.active {
  background-color: var(--color-guc-darkblue);
  border-color: var(--color-guc-darkblue);
  color: #fff;
}

.day-chip.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
